import {
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import BackdropLoading from 'components/common/BackdropLoading';
import { ContainedButton } from 'components/ContainedButton';
import { TFunction } from 'i18next';
import React from 'react';

import { DocumentRequestItem } from './components/document-request-item/document-request-item';
import { DocumentRequestPanel } from './components/document-request-panel';
import ThanksForTrust from './components/thanks-trusting-us';
import { DocumentRequestPhase, UploadRedeemDisplay } from './types';

const useStyles = makeStyles((theme: any) => ({
  cardContent: { padding: theme.spacing(2) },
  confirmContent: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center'
  },
  hint: { color: theme.palette.text.light },
  retryHint: { color: theme.palette.error.main },
}));

const MESSAGE_KEYS: Partial<Record<DocumentRequestPhase, string>> = {
  not_yet: 'documents:document-request-not-yet',
  invalid: 'documents:document-request-invalid',
  used: 'documents:document-request-used',
  expired: 'documents:document-request-expired',
};

type DocumentRequestSceneProps = {
  t: TFunction,
  phase: DocumentRequestPhase,
  documentRequestInfo?: UploadRedeemDisplay,
  isRedeeming: boolean,
  showRedeemRetry: boolean,
  isSendButtonDisable: boolean,
  onConfirm: () => void,
  closeWindow: () => void,
  onAddFile: ({ archiveId, file }: { archiveId: string, file: File }) => void,
  onRemoveFile: ({ archiveId, fileName }: { archiveId: string, fileName: string }) => void,
  uploadIsLoading: boolean,
  onSendFiles: () => void
}

export const DocumentRequestScene = ({
  t,
  phase,
  documentRequestInfo,
  isRedeeming,
  showRedeemRetry,
  isSendButtonDisable,
  onConfirm,
  closeWindow,
  onAddFile,
  onRemoveFile,
  uploadIsLoading,
  onSendFiles
}: DocumentRequestSceneProps) => {
  const classes = useStyles();
  const title = t('documents:upload-required-documents');

  if (phase === 'loading') {
    return <BackdropLoading open loadingText="loading" />;
  }
  if (phase === 'thanks') {
    return <ThanksForTrust t={t} closewindow={closeWindow} />;
  }
  if (phase === 'confirm') {
    return (
      <DocumentRequestPanel title={title}>
        <Box className={classes.confirmContent}>
          <ContainedButton
            text={t('documents:document-request-continue')}
            icon="arrow-right"
            isLoading={isRedeeming}
            disabled={isRedeeming}
            onClick={onConfirm}
          />
          <Typography variant="body2" className={classes.hint}>
            {t('documents:document-request-single-use')}
          </Typography>
          {showRedeemRetry && (
            <Typography variant="body2" role="alert" className={classes.retryHint}>
              {t('auth:error-try-again')}
            </Typography>
          )}
        </Box>
      </DocumentRequestPanel>
    );
  }
  if (phase !== 'upload') {
    return (
      <DocumentRequestPanel title={title}>
        <Box className={classes.confirmContent}>
          <Typography variant="body1" role="status">{t(MESSAGE_KEYS[phase] ?? 'documents:document-request-invalid')}</Typography>
        </Box>
      </DocumentRequestPanel>
    );
  }

  return (
    <DocumentRequestPanel title={title}>
      <Grid
        container
        direction="column"
        spacing={3}
        className={classes.cardContent}
      >
        <Grid item>
          <Typography variant="body1">{t('documents:provide-following-documents')}</Typography>
        </Grid>
        <Grid item container direction="column" spacing={2} wrap="nowrap">
          {documentRequestInfo?.files.map((file) => (
            <DocumentRequestItem
              key={file.archive_id}
              fileRequired={file}
              onAddFile={onAddFile}
              onRemoveFile={onRemoveFile}
            />
          ))}
        </Grid>
        <Grid item container justifyContent="flex-end">
          <ContainedButton
            text={t('documents:send-files')}
            icon="paper-plane"
            isLoading={uploadIsLoading}
            onClick={onSendFiles}
            disabled={isSendButtonDisable || uploadIsLoading}
          />
        </Grid>
      </Grid>
    </DocumentRequestPanel>
  );
};
